import "./dashboard-sections.css";
import DashboardCard from "./DashboardCard";
import { Sun, Moon } from "lucide-react";
import { makePriorLotNumber } from "../../../helpers/Utils";
import { useGetPackingAnalyticsQuery } from "../dashBoardApi";
import useTranslator from "../../../hooks/useTranslator";
import { useEffect } from "react";

const MetricBox = ({ label, value, unit, theme = "orange", isLoading = false }) => (
  <div className={`pko-box pko-box-${theme}`}>
    <div className="pko-box-label">{label}</div>
    <div className="pko-box-value">
      {isLoading
        ? <span className="db-shimmer" style={{ height: 24, width: 60, display: "block" }} />
        : (value ?? "—")
      }
    </div>
    {unit && <div className={`pko-box-unit pko-unit-${theme}`}>{unit}</div>}
  </div>
);

const BatchBadge = ({ startDate, endDate }) => (
  <span className="pko-batch-badge">
    #{makePriorLotNumber(startDate)} - #{makePriorLotNumber(endDate)}
  </span>
);

const PackingOverview = ({ data = [], startDate, endDate }) => {
  const { _t } = useTranslator();

  const {
    data: packingData,
    isLoading: isPckDataLoading,
    refetch : packingDataRefetch
  } = useGetPackingAnalyticsQuery(
    {
      startDate,
      endDate,
      batchFrom: makePriorLotNumber(startDate),
      batchTo: makePriorLotNumber(endDate),
    },
    { skip: !startDate || !endDate }
  );

  const packingDayRespData   = packingData?.data?.packing_day;
  const packingNightRespData = packingData?.data?.packing_night;

  useEffect(() => {
    packingDataRefetch();
  }, []);

  return (
    <div className="po-wrapper">
      {/* PACKING DAY */}
      <DashboardCard
        title={<><Sun size={15} color="#ee7225" style={{ marginRight: 6, flexShrink: 0 }} /> {_t("packing_db", "PACKING")} <span className="pko-shift-label">{_t("day_db", "Day")}</span></>}
        badgeIcon={startDate && endDate ? <BatchBadge startDate={startDate} endDate={endDate} /> : null}
      >
        <div className="pko-grid-5">
          <MetricBox label={_t("total_boxes_db", "TOTAL BOXES")}  value={packingDayRespData?.total_boxes_packed ?? 0}       theme="orange" isLoading={isPckDataLoading} />
          <MetricBox label={_t("cheeks_db", "CHEEKS")}            value={packingDayRespData?.total_cheeks_10lb ?? 0}  unit="10 LB" theme="orange" isLoading={isPckDataLoading} />
          <MetricBox label={_t("cheeks_db", "CHEEKS")}            value={packingDayRespData?.total_cheeks_20lb ?? 0}  unit="20 LB" theme="orange" isLoading={isPckDataLoading} />
          <MetricBox label={_t("strips_db", "STRIPS")}            value={packingDayRespData?.total_strips_20lb ?? 0}  unit="20 LB" theme="orange" isLoading={isPckDataLoading} />
          <MetricBox label={_t("pasting_db", "PASTING")}          value={packingDayRespData?.total_pasted_boxes_packed ?? 0} unit="20 LB" theme="orange" isLoading={isPckDataLoading} />
        </div>
      </DashboardCard>

      {/* PACKING TURN 2 */}
      <DashboardCard
        title={<><Moon size={15} color="#5b6abf" style={{ marginRight: 6, flexShrink: 0 }} /> {_t("packing_db", "PACKING")} <span className="pko-shift-label pko-shift-blue">{_t("turn_2_db", "Turn 2")}</span></>}
        badgeIcon={startDate && endDate ? <BatchBadge startDate={startDate} endDate={endDate} /> : null}
      >
        <div className="pko-turn2-layout">
          <div className="pko-total-cell pko-box-blue">
            <div className="pko-box-label">{_t("total_boxes_day_night_db", "TOTAL BOXES")} <br /> {_t("day_and_night_db", "(DAY & NIGHT)")}</div>
            <div className="pko-box-value">
              {((packingDayRespData?.total_boxes_packed ?? 0) + (packingNightRespData?.total_night_box_packed ?? 0)) || "0"}
            </div>
          </div>
          <div className="pko-grid-right">
            <MetricBox label={_t("total_boxes_db", "TOTAL BOXES")}          value={packingNightRespData?.total_night_box_packed ?? 0}   theme="blue" isLoading={isPckDataLoading} />
            <MetricBox label={_t("prime_cheek_boxes_db", "PRIME CHEEK BOXES")} value={packingNightRespData?.prime_cheeks_box_10lb ?? 0}  unit="10 LB" theme="blue" isLoading={isPckDataLoading} />
            <MetricBox label={_t("prime_cheek_boxes_db", "PRIME CHEEK BOXES")} value={packingNightRespData?.prime_cheeks_box_20lb ?? 0}  unit="20 LB" theme="blue" isLoading={isPckDataLoading} />
            <MetricBox label={_t("prime_strip_boxes_db", "PRIME STRIP BOXES")} value={packingNightRespData?.prime_strips_box_20lb ?? 0}  unit="20 LB" theme="blue" isLoading={isPckDataLoading} />
            <MetricBox label={_t("seconds_cheek_boxes_db", "SECONDS CHEEK BOXES")} value={packingNightRespData?.second_cheeks_box_20lb ?? 0} unit="20 LB" theme="blue" isLoading={isPckDataLoading} />
            <MetricBox label={_t("seconds_strip_boxes_db", "SECONDS STRIP BOXES")} value={packingNightRespData?.second_strips_box_20lb ?? 0} unit="20 LB" theme="blue" isLoading={isPckDataLoading} />
            <MetricBox label={_t("tapitas_boxes_db", "TAPITAS BOXES")}        value={packingNightRespData?.tapita_box_20lb ?? 0}          unit="20 LB" theme="blue" isLoading={isPckDataLoading} />
            <MetricBox label={_t("pieces_boxes_db", "PIECES BOXES")}          value={packingNightRespData?.pieces_box_20lb ?? 0}          unit="20 LB" theme="blue" isLoading={isPckDataLoading} />
          </div>
        </div>
      </DashboardCard>
    </div>
  );
};

export default PackingOverview;
